import { jsPDF } from 'jspdf';

// Define colors similar to the sample
const COLOR_PRIMARY = "#0A2342";     // Deep blue
const COLOR_SECONDARY = "#2D4B6F";   // Medium blue
const COLOR_ACCENT = "#BCA88E";      // Gold/tan
const COLOR_LIGHT = "#E9EFF4";       // Light blue-grey
const COLOR_GOOD = "#2D8B75";        // Good indicator (green)
const COLOR_WARNING = "#E5873D";     // Warning indicator (orange)
const COLOR_ALERT = "#D45E5E";       // Alert indicator (red)
const COLOR_TEXT = "#1A324B";        // Main text
const COLOR_TEXT_LIGHT = "#6A7F9A";  // Light text

export const generateRelaxationReportPDF = async (reportData, patientName, sessionDate, treatment) => {
    try {
        // Create new PDF document
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        // Generate cover page
        createCoverPage(pdf, patientName, treatment, sessionDate);

        // Add second page - Relaxation overview
        pdf.addPage();
        createRelaxationOverview(pdf, reportData, treatment);

        // Add third page - Physiological metrics
        pdf.addPage();
        createPhysiologicalMetrics(pdf, reportData);

        // Add fourth page - Recommendations
        pdf.addPage();
        createRecommendations(pdf, reportData, treatment);

        // Add footer to all pages
        for (let i = 1; i <= pdf.internal.getNumberOfPages(); i++) {
            createFooter(pdf, i);
        }

        // Generate a filename
        const dateStr = formatDateForFileName(sessionDate);
        const fileName = `Relaxation_Report_${patientName.replace(/\s+/g, '_')}_${dateStr}.pdf`;

        // Save the PDF
        pdf.save(fileName);

        return true;
    } catch (error) {
        console.error("Error generating PDF:", error);
        throw error;
    }
};

// Create the cover page
function createCoverPage(pdf, patientName, treatment, sessionDate) {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Top banner
    pdf.setFillColor(hexToRgb(COLOR_PRIMARY).r, hexToRgb(COLOR_PRIMARY).g, hexToRgb(COLOR_PRIMARY).b);
    pdf.rect(0, 0, pageWidth, 70, 'F');

    // Gold accent element
    pdf.setFillColor(hexToRgb(COLOR_ACCENT).r, hexToRgb(COLOR_ACCENT).g, hexToRgb(COLOR_ACCENT).b);
    pdf.rect(15, 70, 5, 70, 'F');
    pdf.rect(pageWidth - 20, 0, 5, 70, 'F');

    // Title text
    pdf.setTextColor(255, 255, 255); // White
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(24);

    const hotelName = treatment?.hotel || "Hotel Specialised for " + patientName;
    pdf.text(hotelName, pageWidth / 2, 30, { align: 'center' });

    // Golden separator
    pdf.setDrawColor(hexToRgb(COLOR_ACCENT).r, hexToRgb(COLOR_ACCENT).g, hexToRgb(COLOR_ACCENT).b);
    pdf.setLineWidth(2);
    pdf.line(pageWidth / 2 - 80, 45, pageWidth / 2 + 80, 45);

    // Subtitle
    pdf.setFontSize(20);
    pdf.text("RELAXATION REPORT", pageWidth / 2, 60, { align: 'center' });

    // Center info box
    pdf.setFillColor(hexToRgb(COLOR_LIGHT).r, hexToRgb(COLOR_LIGHT).g, hexToRgb(COLOR_LIGHT).b);
    pdf.rect(pageWidth / 2 - 80, 100, 160, 100, 'F');

    // Gold border for info box
    pdf.setDrawColor(hexToRgb(COLOR_ACCENT).r, hexToRgb(COLOR_ACCENT).g, hexToRgb(COLOR_ACCENT).b);
    pdf.setLineWidth(1);
    pdf.rect(pageWidth / 2 - 80, 100, 160, 100, 'S');

    // Report info
    pdf.setTextColor(hexToRgb(COLOR_TEXT).r, hexToRgb(COLOR_TEXT).g, hexToRgb(COLOR_TEXT).b);
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");

    // Room info
    const roomInfo = treatment?.roomNumber || "VIP001";
    pdf.text(`Room ${roomInfo}`, pageWidth / 2, 120, { align: 'center' });

    // Date info
    const formattedDate = formatDate(sessionDate);
    pdf.setFont("helvetica", "normal");
    pdf.text(`Date: ${formattedDate}`, pageWidth / 2, 140, { align: 'center' });

    // Device ID
    pdf.setTextColor(hexToRgb(COLOR_TEXT_LIGHT).r, hexToRgb(COLOR_TEXT_LIGHT).g, hexToRgb(COLOR_TEXT_LIGHT).b);
    pdf.setFontSize(12);
    pdf.text(`Device ID: ${treatment?.deviceId || "76C130502BB4"}`, pageWidth / 2, 160, { align: 'center' });

    // Bottom section
    pdf.setFillColor(hexToRgb(COLOR_PRIMARY).r, hexToRgb(COLOR_PRIMARY).g, hexToRgb(COLOR_PRIMARY).b);
    pdf.rect(0, pageHeight - 70, pageWidth, 70, 'F');

    // Bottom text
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(12);
    pdf.text("A Personalized Relaxation Analysis Report", pageWidth / 2, pageHeight - 40, { align: 'center' });

    // Company info
    pdf.setFontSize(10);
    pdf.setTextColor(hexToRgb(COLOR_LIGHT).r, hexToRgb(COLOR_LIGHT).g, hexToRgb(COLOR_LIGHT).b);
    pdf.text("Powered by KAT LAI TECHNOLOGY LIMITED", pageWidth / 2, pageHeight - 25, { align: 'center' });
    pdf.text("© 2025 All Rights Reserved", pageWidth / 2, pageHeight - 15, { align: 'center' });

    // Golden separator at bottom
    pdf.setDrawColor(hexToRgb(COLOR_ACCENT).r, hexToRgb(COLOR_ACCENT).g, hexToRgb(COLOR_ACCENT).b);
    pdf.setLineWidth(2);
    pdf.line(pageWidth / 2 - 60, pageHeight - 10, pageWidth / 2 + 60, pageHeight - 10);
}

// Create page footer
function createFooter(pdf, pageNum) {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    pdf.setPage(pageNum);

    if (pageNum > 1) { // Don't add footer to cover page
        // Footer bar
        pdf.setFillColor(hexToRgb(COLOR_LIGHT).r, hexToRgb(COLOR_LIGHT).g, hexToRgb(COLOR_LIGHT).b);
        pdf.rect(0, pageHeight - 10, pageWidth, 10, 'F');

        // Footer text
        pdf.setTextColor(hexToRgb(COLOR_TEXT_LIGHT).r, hexToRgb(COLOR_TEXT_LIGHT).g, hexToRgb(COLOR_TEXT_LIGHT).b);
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        pdf.text("Powered by KAT LAI TECHNOLOGY LIMITED", 15, pageHeight - 4);
    }
}

// Create relaxation overview section
function createRelaxationOverview(pdf, reportData, treatment) {
    const pageWidth = pdf.internal.pageSize.getWidth();

    // Add header
    createHeader(pdf, treatment?.hotel || "Hotel Specialised for Relaxation", 2);

    // Section title
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.setTextColor(hexToRgb(COLOR_TEXT).r, hexToRgb(COLOR_TEXT).g, hexToRgb(COLOR_TEXT).b);
    pdf.text("Relaxation Overview", 15, 30);

    // Horizontal rule
    pdf.setDrawColor(hexToRgb(COLOR_ACCENT).r, hexToRgb(COLOR_ACCENT).g, hexToRgb(COLOR_ACCENT).b);
    pdf.setLineWidth(1);
    pdf.line(15, 35, pageWidth - 15, 35);

    // Relaxation score
    pdf.setFontSize(36);
    const scoreColorRgb = hexToRgb(getScoreColor(reportData.score));
    pdf.setTextColor(scoreColorRgb.r, scoreColorRgb.g, scoreColorRgb.b);
    pdf.text(reportData.score.toString(), pageWidth / 2, 55, { align: 'center' });

    // Rating
    pdf.setFontSize(18);
    pdf.text(getScoreRating(reportData.score), pageWidth / 2, 70, { align: 'center' });

    // Score explanation
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(hexToRgb(COLOR_TEXT).r, hexToRgb(COLOR_TEXT).g, hexToRgb(COLOR_TEXT).b);
    const scoreDescription = getScoreDescription(reportData.score);

    // Handle long text with paragraph wrapping
    const textLines = pdf.splitTextToSize(scoreDescription, pageWidth - 30);
    pdf.text(textLines, pageWidth / 2, 85, { align: 'center' });

    // Session time information
    const therapistName = treatment?.therapistName || "Your Therapist";
    const treatmentType = treatment?.treatmentType || "Relaxation Therapy";
    const duration = reportData.totalSleep;

    pdf.setFont("helvetica", "normal");
    pdf.text(`You received a ${duration}-minute ${treatmentType} session with ${therapistName}.`, pageWidth / 2, 110, { align: 'center' });

    // Relaxation highlights (bullet points from treatment effect comparison)
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.text("Key Relaxation Metrics", 15, 130);

    // Create metrics boxes
    createMetricBox(pdf, "Total Relaxation", (reportData.totalSleep / 60).toFixed(1), "hours", 40, 145);
    createMetricBox(pdf, "Deep Relaxation", ((reportData.totalSleep * 0.32) / 60).toFixed(1), "hours", 105, 145);
    createMetricBox(pdf, "Heart Rate Change", calculateHeartRateChange(reportData), "bpm", 170, 145);

    // Before-after section
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text("Relaxation Progress", 15, 195);

    // Extract initial and final scores if available
    let initialScore = reportData.initialScore || Math.max(reportData.score - 15, 40);
    let finalScore = reportData.finalScore || reportData.score;

    // Show initial/final comparison as text (simplified)
    pdf.setFontSize(12);
    pdf.text("Beginning of Session:", 30, 215);
    pdf.text("End of Session:", 120, 215);

    // Score values
    pdf.setFontSize(18);
    pdf.setTextColor(hexToRgb(getScoreColor(initialScore)).r, hexToRgb(getScoreColor(initialScore)).g, hexToRgb(getScoreColor(initialScore)).b);
    pdf.text(initialScore.toString(), 55, 235);

    pdf.setTextColor(hexToRgb(getScoreColor(finalScore)).r, hexToRgb(getScoreColor(finalScore)).g, hexToRgb(getScoreColor(finalScore)).b);
    pdf.text(finalScore.toString(), 145, 235);

    // Arrow indicating progress
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(1);
    pdf.line(70, 235, 125, 235);

    // Arrowhead
    pdf.line(125, 235, 120, 232);
    pdf.line(125, 235, 120, 238);

    // Show improvement
    const improvement = Math.round(finalScore - initialScore);
    pdf.setTextColor(improvement > 0 ? hexToRgb(COLOR_GOOD).r : hexToRgb(COLOR_ALERT).r,
        improvement > 0 ? hexToRgb(COLOR_GOOD).g : hexToRgb(COLOR_ALERT).g,
        improvement > 0 ? hexToRgb(COLOR_GOOD).b : hexToRgb(COLOR_ALERT).b);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text((improvement > 0 ? "+" : "") + improvement, 100, 230, { align: 'center' });

    // Compare metrics
    pdf.setTextColor(hexToRgb(COLOR_TEXT).r, hexToRgb(COLOR_TEXT).g, hexToRgb(COLOR_TEXT).b);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);

    pdf.text(`HR: ${Math.round(reportData.avgHr + 7)} BPM`, 50, 260, { align: 'center' });
    pdf.text(`RR: ${Math.round(reportData.avgRr + 2)}/min`, 50, 270, { align: 'center' });

    pdf.text(`HR: ${Math.round(reportData.avgHr)} BPM`, 150, 260, { align: 'center' });
    pdf.text(`RR: ${Math.round(reportData.avgRr)}/min`, 150, 270, { align: 'center' });
}

// Create page header
function createHeader(pdf, hotelName, pageNumber) {
    const pageWidth = pdf.internal.pageSize.getWidth();

    // Top banner
    pdf.setFillColor(hexToRgb(COLOR_PRIMARY).r, hexToRgb(COLOR_PRIMARY).g, hexToRgb(COLOR_PRIMARY).b);
    pdf.rect(0, 0, pageWidth, 15, 'F');

    // Hotel name
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "bold");
    pdf.text(hotelName, 15, 10);

    // Page number
    pdf.text(`Page ${pageNumber}`, pageWidth - 15, 10, { align: 'right' });

    // Gold accent
    pdf.setFillColor(hexToRgb(COLOR_ACCENT).r, hexToRgb(COLOR_ACCENT).g, hexToRgb(COLOR_ACCENT).b);
    pdf.rect(pageWidth - 60, 0, 2, 15, 'F');
}

// Create physiological metrics section
function createPhysiologicalMetrics(pdf, reportData) {
    const pageWidth = pdf.internal.pageSize.getWidth();

    // Add header
    createHeader(pdf, "Hotel Specialised for Relaxation", 3);

    // Section title
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.setTextColor(hexToRgb(COLOR_TEXT).r, hexToRgb(COLOR_TEXT).g, hexToRgb(COLOR_TEXT).b);
    pdf.text("Physiological Metrics", 15, 30);

    // Horizontal rule
    pdf.setDrawColor(hexToRgb(COLOR_ACCENT).r, hexToRgb(COLOR_ACCENT).g, hexToRgb(COLOR_ACCENT).b);
    pdf.setLineWidth(1);
    pdf.line(15, 35, pageWidth - 15, 35);

    // Heart Rate Analysis
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text("Heart Rate Analysis", 15, 50);

    // Heart rate metrics
    const minHR = reportData.minHr || calculateMetric(reportData.avgHr, 0.7);
    const avgHR = reportData.avgHr;
    const maxHR = reportData.maxHr || calculateMetric(reportData.avgHr, 1.3);

    createMetricBox(pdf, "Minimum HR", minHR, "bpm", 40, 65);
    createMetricBox(pdf, "Average HR", avgHR, "bpm", 105, 65);
    createMetricBox(pdf, "Maximum HR", maxHR, "bpm", 170, 65);

    // Simple bar visualization
    pdf.setFillColor(hexToRgb(COLOR_GOOD).r, hexToRgb(COLOR_GOOD).g, hexToRgb(COLOR_GOOD).b);
    pdf.rect(40, 120, 20, 15, 'F');
    pdf.setFillColor(hexToRgb(COLOR_PRIMARY).r, hexToRgb(COLOR_PRIMARY).g, hexToRgb(COLOR_PRIMARY).b);
    pdf.rect(105, 120, 20, 15, 'F');
    pdf.setFillColor(hexToRgb(COLOR_WARNING).r, hexToRgb(COLOR_WARNING).g, hexToRgb(COLOR_WARNING).b);
    pdf.rect(170, 120, 20, 15, 'F');

    // Labels
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.text(minHR.toString(), 50, 130, { align: 'center' });
    pdf.text(avgHR.toString(), 115, 130, { align: 'center' });
    pdf.text(maxHR.toString(), 180, 130, { align: 'center' });

    pdf.setTextColor(hexToRgb(COLOR_TEXT).r, hexToRgb(COLOR_TEXT).g, hexToRgb(COLOR_TEXT).b);
    pdf.setFontSize(8);
    pdf.text("Minimum", 50, 145, { align: 'center' });
    pdf.text("Average", 115, 145, { align: 'center' });
    pdf.text("Maximum", 180, 145, { align: 'center' });

    // Heart Rate Interpretation  
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text("About Heart Rate During Relaxation:", 15, 170);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    const hrExplanation = getHeartRateDescription(reportData.avgHr);
    const hrLines = pdf.splitTextToSize(hrExplanation, pageWidth - 30);
    pdf.text(hrLines, 15, 180);

    // Respiration Analysis
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text("Respiration Analysis", 15, 210);

    // Respiration metrics
    const minRR = reportData.minRr || calculateMetric(reportData.avgRr, 0.7);
    const avgRR = reportData.avgRr;
    const maxRR = reportData.maxRr || calculateMetric(reportData.avgRr, 1.3);

    createMetricBox(pdf, "Minimum RR", minRR, "rpm", 40, 225);
    createMetricBox(pdf, "Average RR", avgRR, "rpm", 105, 225);
    createMetricBox(pdf, "Maximum RR", maxRR, "rpm", 170, 225);

    // Check if "About Respiration" section needs a page break
    // We need approximately 40mm for title and paragraph
    if (needsPageBreak(pdf, 280, 40)) {
        // Add a new page if there's not enough space
        pdf.addPage();
        createHeader(pdf, "Hotel Specialised for Relaxation", 4); // New page number

        // Draw the respiration explanation at the top of the new page
        pdf.setTextColor(hexToRgb(COLOR_TEXT).r, hexToRgb(COLOR_TEXT).g, hexToRgb(COLOR_TEXT).b);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.text("About Respiration During Relaxation:", 15, 30);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        const rrExplanation = getRespirationDescription(reportData.avgRr);
        const rrLines = pdf.splitTextToSize(rrExplanation, pageWidth - 30);
        pdf.text(rrLines, 15, 45); // Position after the title
    } else {
        // If there's enough space, continue on the same page
        pdf.setTextColor(hexToRgb(COLOR_TEXT).r, hexToRgb(COLOR_TEXT).g, hexToRgb(COLOR_TEXT).b);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.text("About Respiration During Relaxation:", 15, 280);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        const rrExplanation = getRespirationDescription(reportData.avgRr);
        const rrLines = pdf.splitTextToSize(rrExplanation, pageWidth - 30);
        pdf.text(rrLines, 15, 290);
    }
}

// Create recommendations section
function createRecommendations(pdf, reportData, treatment) {
    const pageWidth = pdf.internal.pageSize.getWidth();

    // Add header
    createHeader(pdf, "Hotel Specialised for Relaxation", 4);

    // Section title
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.setTextColor(hexToRgb(COLOR_TEXT).r, hexToRgb(COLOR_TEXT).g, hexToRgb(COLOR_TEXT).b);
    pdf.text("Personalized Wellness Recommendations", 15, 30);

    // Horizontal rule
    pdf.setDrawColor(hexToRgb(COLOR_ACCENT).r, hexToRgb(COLOR_ACCENT).g, hexToRgb(COLOR_ACCENT).b);
    pdf.setLineWidth(1);
    pdf.line(15, 35, pageWidth - 15, 35);

    // Introduction text
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.text("Based on your unique relaxation profile, we've crafted the following recommendations to enhance your wellbeing:", 15, 45);

    // Generate recommendations based on metrics
    const recommendations = generateRecommendations(reportData);

    // Add recommendations as bullet points
    pdf.setFontSize(11);
    let yPos = 60;

    recommendations.forEach((rec, i) => {
        pdf.setFont("helvetica", "bold");
        pdf.text(`${i + 1}.`, 15, yPos);

        pdf.setFont("helvetica", "normal");
        const recLines = pdf.splitTextToSize(rec, pageWidth - 40);
        pdf.text(recLines, 25, yPos);

        yPos += 10 * (1 + Math.floor(rec.length / 80)); // Adjust position based on text length
    });

    // ALWAYS START PREMIUM SERVICES ON A NEW PAGE
    pdf.addPage();
    createHeader(pdf, "Hotel Specialised for Relaxation", 5); // Update page number

    // Reset yPos for the new page
    yPos = 30;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text("Premium Hotel Services", 15, yPos);

    yPos += 10;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.text("Enhance your stay with these personalized wellness services, designed to promote optimal relaxation:", 15, yPos);

    // Services (simplified boxes)
    yPos += 15;
    const services = generateServices(reportData);

    services.forEach((service, i) => {
        const [name, description] = service.split(':');

        // Each service box needs approximately 30mm
        if (i > 0 && needsPageBreak(pdf, yPos, 30)) {
            pdf.addPage();
            createHeader(pdf, "Hotel Specialised for Relaxation", 6); // Update page number
            yPos = 30; // Reset to top of page
        }

        // Service box background
        pdf.setFillColor(hexToRgb(COLOR_LIGHT).r, hexToRgb(COLOR_LIGHT).g, hexToRgb(COLOR_LIGHT).b);
        pdf.rect(15, yPos, pageWidth - 30, 25, 'F');

        // Gold accent
        pdf.setFillColor(hexToRgb(COLOR_ACCENT).r, hexToRgb(COLOR_ACCENT).g, hexToRgb(COLOR_ACCENT).b);
        pdf.rect(15, yPos, 5, 25, 'F');

        // Service name and description
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.setTextColor(hexToRgb(COLOR_SECONDARY).r, hexToRgb(COLOR_SECONDARY).g, hexToRgb(COLOR_SECONDARY).b);
        pdf.text(name, 25, yPos + 10);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        pdf.setTextColor(hexToRgb(COLOR_TEXT).r, hexToRgb(COLOR_TEXT).g, hexToRgb(COLOR_TEXT).b);

        const descLines = pdf.splitTextToSize(description, pageWidth - 60);
        pdf.text(descLines, 25, yPos + 18);

        yPos += 30;
    });

    // Booking information
    yPos += 5;
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(10);
    pdf.setTextColor(hexToRgb(COLOR_TEXT).r, hexToRgb(COLOR_TEXT).g, hexToRgb(COLOR_TEXT).b);
    pdf.text("To book any of these services, please contact the front desk or speak with our Sleep Concierge", 15, yPos);
    pdf.text("through the hotel app.", 15, yPos + 10);

    // Acknowledgment and disclaimer
    yPos += 30;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.text("Acknowledgement", 15, yPos);

    yPos += 10;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    const acknowledgment = "This report is provided with the technical support of KAT LAI TECHNOLOGY LIMITED. We are committed to helping you improve your relaxation quality, ensuring you feel refreshed and energized.";
    const ackLines = pdf.splitTextToSize(acknowledgment, pageWidth - 30);
    pdf.text(ackLines, 15, yPos);

    // Disclaimer
    yPos += 20;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.text("Disclaimer", 15, yPos);

    yPos += 10;
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(9);
    const disclaimer = "This report is for reference purposes only and is not intended for medical diagnosis or treatment. The data provided is based on non-medical-grade devices and may have some variations. Please consult a medical professional for any health concerns.";
    const discLines = pdf.splitTextToSize(disclaimer, pageWidth - 30);
    pdf.text(discLines, 15, yPos);
}

// Helper functions
function createMetricBox(pdf, title, value, unit, x, y) {
    const boxWidth = 55;
    const boxHeight = 40;

    // Box background
    pdf.setFillColor(hexToRgb(COLOR_LIGHT).r, hexToRgb(COLOR_LIGHT).g, hexToRgb(COLOR_LIGHT).b);
    pdf.rect(x - boxWidth / 2, y, boxWidth, boxHeight, 'F');

    // Title bar
    pdf.setFillColor(hexToRgb(COLOR_PRIMARY).r, hexToRgb(COLOR_PRIMARY).g, hexToRgb(COLOR_PRIMARY).b);
    pdf.rect(x - boxWidth / 2, y, boxWidth, 15, 'F');

    // Title text
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text(title, x, y + 10, { align: 'center' });

    // Value
    pdf.setTextColor(hexToRgb(COLOR_TEXT).r, hexToRgb(COLOR_TEXT).g, hexToRgb(COLOR_TEXT).b);
    pdf.setFontSize(14);
    pdf.text(value.toString(), x, y + 30, { align: 'center' });

    // Unit
    pdf.setFontSize(8);
    pdf.setTextColor(hexToRgb(COLOR_TEXT_LIGHT).r, hexToRgb(COLOR_TEXT_LIGHT).g, hexToRgb(COLOR_TEXT_LIGHT).b);
    pdf.text(unit, x, y + 37, { align: 'center' });
}

function calculateMetric(avg, factor) {
    return Math.round(avg * factor);
}

function calculateHeartRateChange(reportData) {
    // If we have raw data, calculate actual change
    if (reportData.rawData?.heartRate?.values) {
        const heartRateData = reportData.rawData.heartRate.values;
        const initialSegmentSize = Math.max(Math.floor(heartRateData.length * 0.15), 1);
        const finalSegmentSize = Math.max(Math.floor(heartRateData.length * 0.15), 1);

        const initialHR = heartRateData.slice(0, initialSegmentSize);
        const finalHR = heartRateData.slice(-finalSegmentSize);

        const avgInitialHR = initialHR.reduce((sum, val) => sum + val, 0) / initialHR.length;
        const avgFinalHR = finalHR.reduce((sum, val) => sum + val, 0) / finalHR.length;

        return "-" + Math.abs(Math.round(avgInitialHR - avgFinalHR));
    }

    // Fallback to a reasonable estimate
    return "-7";
}

function generateRecommendations(reportData) {
    const score = reportData.score;
    const hr = reportData.avgHr;
    const rr = reportData.avgRr;

    const recommendations = [];

    // Base recommendations on metrics
    if (score < 70) {
        recommendations.push("Consider scheduling regular relaxation sessions to build on the progress you've made today.");
    }

    if (hr > 75) {
        recommendations.push("Your heart rate during relaxation was slightly elevated. Try practicing deep breathing exercises before your next treatment to enhance the calming effect.");
    }

    if (rr > 16) {
        recommendations.push("Your breathing rate could benefit from focused breath work. Ask your therapist about guided breathing techniques for your next session.");
    }

    // Add general recommendations
    recommendations.push("Maintain the relaxation benefits by avoiding screens and stressful activities for at least 30 minutes after your treatment.");
    recommendations.push("Creating a comfortable sleep environment with optimal temperature (65-68°F/18-20°C) can help maintain the relaxation effects of your treatment.");

    // Ensure we have at least 3 recommendations
    if (recommendations.length < 3) {
        recommendations.push("Consider booking our signature aromatic relaxation treatment next time, which combines multiple techniques for enhanced benefits.");
    }

    return recommendations;
}

function generateServices(reportData) {
    const services = [
        "Herbal Tea Ritual: Our special Earth blend, served 90 minutes before your desired bedtime, can promote continued relaxation.",
        "Extended Relaxation Session: Building on your positive response today, consider our 90-minute extended treatment package.",
        "Aromatherapy Add-On: Enhance your next session with our signature essential oil blends designed to deepen relaxation."
    ];

    return services;
}

function formatDateForFileName(date) {
    if (!date) return 'unknown_date';
    const d = new Date(date);
    return d.toISOString().split('T')[0].replace(/-/g, '');
}

function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).replace(/(\d+)\/(\d+)\/(\d+)/, '$3/$1/$2'); // Convert to YYYY/MM/DD format
}

// Functions to determine colors and text based on score
function getScoreColor(score) {
    if (score >= 90) return "#2D8B75"; // Green
    if (score >= 75) return "#4CAF50"; // Light Green
    if (score >= 60) return "#FFB74D"; // Amber
    if (score >= 50) return "#FF9800"; // Orange
    return "#F44336"; // Red
}

function getScoreRating(score) {
    if (score >= 90) return "Excellent";
    if (score >= 75) return "Very Good";
    if (score >= 60) return "Good";
    if (score >= 50) return "Fair";
    return "Limited";
}

function getScoreDescription(score) {
    if (score >= 90) return "Your relaxation response was excellent! You achieved a deep state of calm during the treatment that will have lasting benefits.";
    if (score >= 75) return "You experienced very good relaxation during your treatment. Your body showed clear signs of the parasympathetic response associated with stress reduction.";
    if (score >= 60) return "Your relaxation level was good. The treatment produced a positive calming effect that you can build upon with regular sessions.";
    if (score >= 50) return "You achieved a fair level of relaxation. The guest showed moderate signs of relaxation but may benefit from enhanced techniques.";
    return "Limited relaxation was detected during this session. This could be due to external factors or stress. Consider discussing personalized techniques with your therapist.";
}

function getHeartRateDescription(hr) {
    if (hr < 60) return "Your heart rate during treatment indicates an excellent relaxation response. A lower heart rate is associated with activation of the parasympathetic nervous system, which promotes healing and recovery.";
    if (hr < 70) return "Your heart rate shows a good relaxation response. During effective relaxation treatments, heart rate typically decreases as the body enters a rest-and-digest state.";
    if (hr < 80) return "Your heart rate indicates a moderate relaxation response. With continued practice, you may experience deeper levels of relaxation in future sessions.";
    return "Your heart rate remained somewhat elevated during treatment. This can be affected by various factors including recent physical activity, caffeine intake, or stress levels before the session.";
}

function getRespirationDescription(rr) {
    if (rr < 12) return "Your breathing rate during treatment indicates deep relaxation. Slow, steady breathing helps activate the parasympathetic nervous system, enhancing the therapeutic benefits of your treatment.";
    if (rr < 15) return "Your breathing rate shows a good relaxation response. Relaxed breathing patterns are associated with reduced stress and improved mental clarity.";
    if (rr < 18) return "Your breathing pattern showed moderate relaxation. Regular relaxation practices can help decrease respiration rate over time, leading to deeper relaxation states.";
    return "Your breathing rate remained somewhat elevated. Focused breathing exercises before your next treatment may help deepen your relaxation response.";
}

// Hex to RGB conversion for jsPDF compatibility
function hexToRgb(hex) {
    // Remove # if present
    hex = hex.replace(/^#/, '');

    // Parse hex values
    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return { r, g, b };
}

// Add this helper function

function needsPageBreak(pdf, currentY, contentHeight) {
    const pageHeight = pdf.internal.pageSize.getHeight();
    const footerMargin = 15; // Space reserved for footer
    const availableSpace = pageHeight - currentY - footerMargin;

    return availableSpace < contentHeight;
}